import type {
  QueuedReason,
  SpendLimitScope,
  TaskView,
  TaskExecutionStatus,
  TaskExecutionSummary,
} from '@/lib/api/generated/model';
import { hostsFromCredentials, type MissingHost } from '@/features/identity';
import type { BoardStepSection } from './boardModel';

/**
 * UI-only derivation of a card's execution state. Recomputed fresh on every
 * render from the current TaskView and the step it currently sits on.
 * Not a stored entity, not cached.
 *
 * Deliberately *not* named `TaskStatus`: that name is already bound by the
 * generated client, where it is a single-member enum whose only value is
 * `'active'`. This is a view's reading of several fields, not a stored one.
 *
 * `idle` used to mean three unrelated things at once — never run, finished,
 * and resting mid-board — which nothing surfaced while it rendered nothing.
 * The moment a surface has to *name* a state, that collapse becomes a lie, so
 * the three are separate here.
 */
export type CardExecutionState =
  | { kind: 'not-started' }
  | {
      kind: 'live';
      /**
       * Set when the live run is a `linked_workflow` join waiting for the
       * tasks it created (spec 140 US5): told as waiting, without a spinner.
       * Still `live` for filters, counts and polling, which it is.
       */
      children?: ChildrenWaiting;
    }
  | { kind: 'failed'; stepLabel: string; message: string }
  /**
   * Refused before dispatch because the workspace reached its spend limit
   * (spec 072 FR-022). Deliberately its own kind rather than a flavour of
   * `failed`: nothing went wrong with the work, and the two need different
   * responses from different people — a failure is investigated, a block is
   * released by an administrator raising a limit.
   */
  | {
      kind: 'blocked';
      stepLabel: string;
      message: string;
      /**
       * Which spend limit refused the run (spec 080 FR-014, FR-019).
       *
       * A value, not a word read out of `message`. Two tiers now block runs and they
       * are released by different people — a workspace's cap and one workflow's cap —
       * so a surface that had to string-match the sentence would re-implement the
       * distinction, and would do it differently each time.
       *
       * `null` for a run blocked before this field existed. The blocked state stays
       * usable without it: the message still says which limit in words.
       */
      scope: SpendLimitScope | null;
    }
  /**
   * Refused at its own start: the workspace requires its person's own forge
   * account, and a host had none usable (spec 160). Its own kind rather than a
   * flavour of `blocked`, which every surface reads as a spend limit — a
   * missed check there would tell the reader to ask an administrator about a
   * budget, when the fix is the person linking their account.
   */
  | {
      kind: 'needs-account';
      stepLabel: string;
      hosts: MissingHost[];
      /** The run's person, who alone can link; null if none was recorded. */
      personId: string | null;
      personName: string | null;
    }
  /**
   * Interrupted by a person (spec 078 FR-006).
   *
   * Its own kind rather than a flavour of `failed` or of `blocked`, for the
   * reason `blocked` is separate from `failed`: nothing went wrong, and nobody
   * needs to do anything about it. It also carries no message. A failure and a
   * block each say something the reader could not guess — which step, which
   * limit, whose limit — where every stopped run says the same sentence, and a
   * popover whose content is a constant is a click that teaches nothing.
   */
  | { kind: 'stopped' }
  /**
   * Waiting its turn under a concurrency limit (spec 115, service spec 109).
   *
   * Its own kind, not a flavour of `live`: nothing is running yet, so the spinner
   * `live` shows would be false, and "why hasn't it started?" has an answer here
   * that `live` has no room for. Both fields are `null` from a service that
   * predates them.
   */
  | {
      kind: 'queued';
      position: number | null;
      reason: QueuedReason | null;
    }
  | { kind: 'waiting-human'; reason: WaitingReason }
  | { kind: 'completed' }
  | { kind: 'idle' };

/**
 * Why a task that is waiting has stopped. One status is shown for both — a
 * person scanning a board cannot be asked to know that a pause and a branch
 * mean the same thing — but the detail surface has room to say which.
 */
export type WaitingReason = 'human-gate' | 'branching';

/**
 * Shown when a failure was recorded with no message of its own. Exported so
 * the card's failure panel and the task detail read the same words: two
 * surfaces quoting the same failure must not be able to disagree.
 */
export const NO_FAILURE_DETAIL = 'The step failed with no further detail.';

/**
 * Shown when a run was blocked and the service recorded no reason of its own.
 *
 * The service always sends one, and it deliberately carries no figures — a workspace
 * member may see neither the limit nor the spend (spec 073 FR-023). This fallback keeps
 * that property when the message is missing rather than inventing a number to fill it.
 */
export const NO_BLOCK_DETAIL =
  'This run was stopped by a spend limit. Contact your organization administrator.';

/**
 * What to call the limit that stopped a run (spec 080 FR-019).
 *
 * Two surfaces render a blocked task, and both take the words from here — two places
 * naming the same tier differently is how a reader learns to distrust both, which is the
 * rule `thresholdStyles.ts` already follows for threshold states.
 *
 * Keyed on the value the service sends rather than read out of the message, and it names
 * the *tier*, not the workflow. Naming the workflow itself would need `workflow_name` on
 * the task contract, which carries only `workflow_id` today — a figure invented here
 * would be a guess, and the service's own sentence already says "this workflow" in words.
 */
export const BLOCKED_SCOPE_LABEL: Record<SpendLimitScope, string> = {
  workspace: 'Workspace spend limit',
  workflow: 'Workflow spend limit',
};

/**
 * A parent whose `linked_workflow` run is waiting for the tasks it created
 * (spec 140 US5). Nothing is running on the parent itself — the work is in
 * the children — so it is told as waiting, not as in progress.
 */
export interface ChildrenWaiting {
  /** Created tasks not yet Done. */
  open: number;
  total: number;
  /** The children's workflow, or `null` once it has been deleted. */
  workflowName: string | null;
}

/**
 * Whether `task` is a parent waiting on its children, and on how many. Only
 * while its latest run is `awaiting_children` *and* the join is open. The
 * service sends `linked_tasks` only for an open join: it is `null` once the
 * join has completed or the task has moved on (`TaskView.linked_tasks`,
 * service `tasks/contracts.py`). A run whose status still reads
 * `awaiting_children` without it is not waiting on anything.
 */
export function waitingOnChildren(
  task: Pick<TaskView, 'executions' | 'linked_tasks'>,
): ChildrenWaiting | null {
  const linked = task.linked_tasks;
  if (!linked) return null;
  if (latestExecution(task.executions ?? [])?.status !== 'awaiting_children') {
    return null;
  }
  return {
    open: Math.max(linked.total - linked.done, 0),
    total: linked.total,
    workflowName: linked.workflow_name ?? null,
  };
}

/**
 * Non-terminal set (drives the spinner, per R1).
 */
const NON_TERMINAL_STATUSES = new Set<TaskExecutionStatus>([
  'initial',
  // Waiting for a slot (spec 115). Not in progress, but not finished either: the
  // board keeps polling so it sees the run start, and Stop stays offered.
  'queued',
  'orchestrating',
  'provisioning',
  'submitting',
  'submitted',
  'running',
  // A `linked_workflow` join stays open here, not terminal, until every task
  // it created reaches its own workflow's Done step (spec 097, models.py).
  'awaiting_children',
]);

/**
 * Whether a run has finished, however it finished.
 *
 * Reads the set above rather than restating it: a second list of statuses is a second
 * thing to update when one is added, and the two disagreeing is invisible until
 * something behaves oddly for exactly one status.
 */
export function isTerminalStatus(status: TaskExecutionStatus): boolean {
  return !NON_TERMINAL_STATUSES.has(status);
}

/**
 * The most recent run by `created_at`, id breaking ties. Both callers below
 * need it and both used to sort for themselves; one copy means the two can
 * never disagree about which run is the latest.
 */
export function latestExecution(
  executions: TaskExecutionSummary[],
): TaskExecutionSummary | undefined {
  return newestFirst(executions)[0];
}

/** The step kinds whose run does work: an agent's, or a linked workflow's. */
const REPEATABLE_STEP_KINDS: ReadonlySet<string> = new Set([
  'ai',
  'linked_workflow',
]);

/**
 * Whether Run again would repeat work (spec 249). It repeats the latest run on
 * that run's step, and in Backlog, Done or at a gate the latest run is the
 * pass-through record of arriving there: the service skips it at once, so the
 * person got one more "Passed Done" line and nothing done. The same two kinds
 * the service runs (`_AI_BEARING_STEP_KINDS`).
 */
export function canRunAgain(executions: TaskExecutionSummary[]): boolean {
  const latest = latestExecution(executions);
  return (
    latest !== undefined && REPEATABLE_STEP_KINDS.has(latest.step_kind_snapshot)
  );
}

/**
 * The model a person should be told this task is running (or ran) under.
 *
 * Reads execution snapshots exclusively — never the workflow step's
 * *declared* `llm`, which names an LLM profile's slug (e.g. `"verify-profile"`),
 * not a model (e.g. `"claude-sonnet-5"`). A move always creates the current
 * step's execution row up front, with its model already snapshotted (067),
 * before the run itself starts — so the current step's own execution already
 * has the real answer the moment a task is "in progress" on it, not only
 * once it finishes.
 *
 * Prefers the execution snapshotted for `currentStepId` specifically. Falls
 * back to the most recent execution that carries a model at all when the
 * current step's own row has none — covers the terminal "done" step (its own
 * synthetic, `skipped` execution never has a model) and a human gate with no
 * profile of its own, both of which should still read as "whatever last
 * actually ran" rather than blank. `undefined` only when no execution has
 * ever carried a model snapshot.
 */
export function displayModel(
  currentStepId: string | undefined,
  executions: TaskExecutionSummary[],
): string | undefined {
  const ordered = newestFirst(executions);
  const forCurrentStep = ordered.find(
    (e) => e.step_id_snapshot === currentStepId && e.model_snapshot,
  );
  if (forCurrentStep) return forCurrentStep.model_snapshot ?? undefined;
  return ordered.find((e) => e.model_snapshot)?.model_snapshot ?? undefined;
}

/**
 * Runs in the order a person reads them: most recent first, id breaking a tie.
 *
 * Exported so the run history and the status derivation order runs by one rule.
 * A history whose first row is not the run the status was derived from would be
 * two answers to "what happened last".
 */
export function newestFirst<T extends TaskExecutionSummary>(
  executions: T[],
): T[] {
  return [...executions].sort((a, b) => {
    if (a.created_at !== b.created_at) {
      return a.created_at < b.created_at ? 1 : -1;
    }
    return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
  });
}

/**
 * True if this task's latest execution (by created_at) has a non-terminal status,
 * or if chain_in_progress is set (R1).
 */
export function hasLiveExecution(task: TaskView): boolean {
  if (task.chain_in_progress) return true;

  const latest = latestExecution(task.executions ?? []);
  return latest ? NON_TERMINAL_STATUSES.has(latest.status) : false;
}

/**
 * True if the task is live — used for the client-side busy-drag refusal (R5).
 */
export function isBusy(task: TaskView): boolean {
  return hasLiveExecution(task);
}

/**
 * Derive the card's execution state, first match wins:
 *
 * 0. queued          — the latest run waits for a slot (spec 115); nothing runs yet
 * 1. live            — something is happening now, which outranks everything
 * 2. failed          — the latest run broke and nothing has started since
 * 2b. blocked        — the latest run was refused before it could start
 * 3. not-started     — no run has ever happened, wherever the card sits
 * 4. waiting-human   — a gate wants a decision
 * 5. waiting-human   — a branch wants a decision between paths
 * 6. completed       — resting on the board's terminal column
 * 7. idle            — none of the above
 *
 * Rule 3 sits above 4-6 on purpose. "Not Started" is about whether work has
 * happened, not about where the card is; without this ordering an untouched
 * task dropped straight onto a gate would claim to be waiting on a decision
 * that nothing has asked for.
 *
 * Rules 4 before 5: a step that is both a gate and a branch reports the gate,
 * because that is the more specific thing to tell a person.
 *
 * currentStep is looked up by the caller to avoid duplicate work.
 */
export function deriveCardExecutionState(
  task: TaskView,
  currentStep: BoardStepSection | undefined,
): CardExecutionState {
  // 0. queued — ahead of live, which would otherwise claim it: a queued run is
  // non-terminal, but nothing is running, so a spinner would be false.
  const latestRun = latestExecution(task.executions ?? []);
  if (latestRun?.status === 'queued') {
    return {
      kind: 'queued',
      position: latestRun.queue_position ?? null,
      reason: latestRun.queued_reason ?? null,
    };
  }

  // 1. live — or waiting on the tasks its run created, which is live too.
  if (hasLiveExecution(task)) {
    const children = waitingOnChildren(task);
    return children ? { kind: 'live', children } : { kind: 'live' };
  }

  const executions = task.executions ?? [];

  // 2. failed, and 2b. blocked
  //
  // Read from the same latest run and kept adjacent, because they are the two terminal
  // states that stop a chain. They are reported separately because the reader's next
  // action differs entirely: a failure is investigated here, a block is released by an
  // administrator elsewhere.
  if (executions.length > 0) {
    const latest = latestExecution(executions);
    if (latest && latest.status === 'failed') {
      return {
        kind: 'failed',
        stepLabel: latest.step_id_snapshot || '(unknown step)',
        message: latest.error_message?.trim() || NO_FAILURE_DETAIL,
      };
    }
    // Before the step-derived states below, and beside the other two terminal
    // readings of the latest run: a stopped task is very often standing on a
    // human gate, and reporting "Waiting on you" for a run someone just
    // interrupted is how the board came to say nothing had happened at all.
    if (latest && latest.status === 'stopped') {
      return { kind: 'stopped' };
    }
    if (
      latest &&
      latest.status === 'blocked' &&
      latest.credential_source === 'required'
    ) {
      return {
        kind: 'needs-account',
        stepLabel: latest.step_id_snapshot || '(unknown step)',
        hosts: hostsFromCredentials(latest.forge_credentials),
        personId: latest.started_by?.id ?? null,
        personName: latest.started_by?.display_name ?? null,
      };
    }
    if (latest && latest.status === 'blocked') {
      return {
        kind: 'blocked',
        stepLabel: latest.step_id_snapshot || '(unknown step)',
        // The service's own words, which name a spend limit and the administrator to
        // ask, and contain no figure. Nothing here adds one.
        message: latest.error_message?.trim() || NO_BLOCK_DETAIL,
        scope: latest.blocked_scope ?? null,
      };
    }
  }

  // 3. not-started — nothing has ever run, and no chain is about to.
  // Above the step-derived states so an untouched card is never described by
  // the step it happens to be sitting on.
  if (executions.length === 0) {
    return { kind: 'not-started' };
  }

  if (!currentStep) {
    // Work has happened but there is no step to read — nothing more can be
    // said than that the task is at rest.
    return { kind: 'idle' };
  }

  // 4. waiting-human, because a gate wants a decision
  if (currentStep.kind === 'human_gate') {
    return { kind: 'waiting-human', reason: 'human-gate' };
  }

  // 5. waiting-human, because a branch wants one. A step with more than one
  // outgoing transition cannot advance on its own.
  if (currentStep.transitions && currentStep.transitions.length > 1) {
    return { kind: 'waiting-human', reason: 'branching' };
  }

  // 6. completed — only the board's manufactured terminal column counts.
  // A phase may declare its own step of `done` *type*, which is an ordinary
  // step in the middle of a workflow and emphatically not the end of one.
  if (currentStep.systemRole === 'done') {
    return { kind: 'completed' };
  }

  // 7. idle
  return { kind: 'idle' };
}

/**
 * True if any visible task has a non-terminal execution or chain-in-progress.
 * Used by BoardPage to compute the polling interval (R4).
 */
export function hasNonTerminalActivity(tasks: TaskView[]): boolean {
  return tasks.some((task) => hasLiveExecution(task));
}
